import React, { useEffect, useRef } from 'react';
import './Home.css';
import videoImg from './images/home/home-image1.png';
import pres from './images/headshots/pres.jpeg';
import serviceImg from './images/home/home-service-pic.jpeg';
import inclusionImg from './images/home/home-inclusion-pic.jpeg';
import leadershipImg from './images/home/home-leadership-pic.png';
import unityImg from './images/home/home-unity-pic.jpeg';
import curiosityImg from './images/home/home-curiosity-pic.jpg';

// Import logos for companies section
import abercrombie from './images/home/home-company-logos/abercrombie.png';
import accenture from './images/home/home-company-logos/accenture.png';
import amazon from './images/home/home-company-logos/amazon.png';
import bcg from './images/home/home-company-logos/bcg.png';
import bofa from './images/home/home-company-logos/bofa.png';
import capone from './images/home/home-company-logos/capital-one.png';
import deloitte from './images/home/home-company-logos/deloitte.png';
import ey from './images/home/home-company-logos/ey.png';
import gap from './images/home/home-company-logos/gap.png';
import goldman from './images/home/home-company-logos/goldman.png';
import honda from './images/home/home-company-logos/honda.png';
import jpm from './images/home/home-company-logos/jp-morgan.png';
import kroger from './images/home/home-company-logos/kroger.png';
import levis from './images/home/home-company-logos/levis.png';
import mckinsey from './images/home/home-company-logos/mckinsey.png';
import microsoft from './images/home/home-company-logos/microsoft.png';
import nationwide from './images/home/home-company-logos/nationwide.png';
import oracle from './images/home/home-company-logos/oracle.png';
import pwc from './images/home/home-company-logos/pwc.png';
import spacex from './images/home/home-company-logos/spacex.png';
// Add more imports as needed

export default function Home() {
    const videoUrl = 'https://s3.us-east-2.amazonaws.com/bpadohiostate.com/home-drone-shot.mp4';
    const videoRef = useRef(null);
    const imageRef = useRef(null);
    const textRef = useRef(null);
    const pillarsRef = useRef(null);
    const pillarsSectionRef = useRef(null);
    const pillarRefs = useRef([]);
    const logoRefs = useRef([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    if (imageRef.current) {
                        imageRef.current.classList.add('fade-in');
                    }
                }
            },
            {
                threshold: 0.1, // Trigger when 10% of the image is visible
            }
        );

        const textObserver = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    if (textRef.current) {
                        textRef.current.classList.add('fade-in');
                    }
                }
            },
            {
                threshold: 0.1, // Trigger when 10% of the text is visible
            }
        );

        if (videoRef.current) {
            observer.observe(videoRef.current);
        }

        if (imageRef.current) {
            observer.observe(imageRef.current);
        }

        if (textRef.current) {
            textObserver.observe(textRef.current);
        }

        return () => {
            if (videoRef.current) {
                observer.observe(videoRef.current);
            }
            if (imageRef.current) {
                observer.unobserve(imageRef.current);
            }
            if (textRef.current) {
                textObserver.unobserve(textRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (pillarsRef.current && pillarsSectionRef.current) {
                const scrollTop = window.pageYOffset;
                const textSpeed = 0.5; // Adjust this value for desired text speed
                const bgSpeed = 0.2; // Adjust this value for desired background speed
                pillarsRef.current.style.transform = `translateY(${scrollTop * textSpeed}px)`;
                pillarsSectionRef.current.style.backgroundPositionY = `${scrollTop * bgSpeed}px`;
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Scroll-scrubbed "ping-pong" motion for the pillars. Each pillar's
    // horizontal offset is a pure function of its position in the viewport, so
    // it glides as you scroll, settles to zero when centered, and rewinds when
    // you scroll back up. Adjacent pillars travel opposite ways (tied to the
    // existing left/right layout) for the ping-pong rhythm, and a brand-red
    // accent bar leads the motion via the --accent-x custom property.
    useEffect(() => {
        const pillars = pillarRefs.current.filter(Boolean);
        if (!pillars.length) return;

        // Respect users who ask for less motion: show pillars static + readable.
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (reduceMotion.matches) {
            pillars.forEach(el => {
                el.style.opacity = '1';
                el.style.transform = 'none';
                el.style.setProperty('--accent-x', '0px');
            });
            return;
        }

        let ticking = false;
        const updatePillars = () => {
            ticking = false;
            const vh = window.innerHeight;
            // Scale the travel with viewport width so it stays subtle — and never
            // clips — on narrow screens (body has overflow-x: hidden).
            const rowMax = Math.min(50, window.innerWidth * 0.035);
            const accentMax = rowMax * 1.8; // red bar leads the row's motion
            pillars.forEach(el => {
                const r = el.getBoundingClientRect();
                // Signed distance from viewport center: 0 = centered (settled),
                // >0 = below center (entering), <0 = above center (exiting).
                let delta = (r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2);
                delta = Math.max(-1, Math.min(1, delta));
                const direction = el.classList.contains('pillar-right') ? 1 : -1;
                el.style.transform = `translateX(${direction * delta * rowMax}px)`;
                el.style.setProperty('--accent-x', `${direction * delta * accentMax}px`);
                el.style.opacity = String(Math.max(0, Math.min(1, 1.15 - Math.abs(delta))));
            });
        };

        const onScroll = () => {
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(updatePillars);
            }
        };

        // body is the scroll container here, not window; capture-phase catches
        // its scroll events, and getBoundingClientRect keeps the math correct
        // regardless of which element actually scrolls.
        window.addEventListener('scroll', onScroll, { passive: true, capture: true });
        window.addEventListener('resize', onScroll, { passive: true });
        updatePillars(); // set initial positions

        return () => {
            window.removeEventListener('scroll', onScroll, { capture: true });
            window.removeEventListener('resize', onScroll);
        };
    }, []);

    useEffect(() => {
        const logoObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('fade-in');
                    }
                });
            },
            {
                threshold: 0.1,
            }
        );

        logoRefs.current.forEach(logo => {
            if (logo) {
                logoObserver.observe(logo);
            }
        });

        return () => {
            logoRefs.current.forEach(logo => {
                if (logo) {
                    logoObserver.unobserve(logo);
                }
            });
        };
    }, []);

    const handleScrollDown = () => {
        const homeSection = document.querySelector('.home-section');
        if (homeSection) {
            homeSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="home-page">
            <div className="video-section">
                <video
                    ref={videoRef}
                    autoPlay
                    loop
                    muted
                    playsInline
                    disablePictureInPicture
                    onTouchStart={(e) => e.preventDefault()} // Prevent zooming when tapping the video
                >
                    <source src={videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="content">
                    <div className="text-content">
                        <h2>BUCKEYE PROFESSIONAL ADVANCEMENT & DEVELOPMENT</h2>
                        <div className="container">
                            <p className="typewriter">Developing The Leaders Of Tomorrow.</p>
                        </div>
                    </div>
                    <a href="https://docs.google.com/forms/d/e/1FAIpQLSdbcBxXC67-rP6dDS0uZfXQwcXK3aFhRfXPCjeLIbW62fjexw/viewform" target="_blank" class="recruitment-button">FALL 2026 APPLICATION</a>
                </div>
                <div className="scroll-arrow" onClick={handleScrollDown}>
                    <span className="arrow"></span>
                    <span className="scroll-text">SCROLL DOWN</span>
                </div>
            </div>
            <div className="home-section">
                <div className="home-content">
                    <img src={pres} alt="Meg Newcomb" className="home-image" ref={imageRef} />
                    <div className="home-text" ref={textRef}>
                        <h2>A FEW WORDS FROM THE PRESIDENT</h2>
                        <p>
                            On behalf of the members of Buckeye Professional Advancement and Development, I want to thank you for visiting our website. BPAD has established itself as one of the premier student organizations at The Ohio State University, and it is with great pride and excitement that we invite you to learn more about who we are and what we stand for.
                            <br /><br />
                            At our core, BPAD is committed to excellence. We hold ourselves to a high standard in everything we do. From the way we develop professionally, to how we lead, serve, and support one another. Our members are driven, motivated, and intentional about their growth, continuously pushing themselves and each other to reach their full potential.
                            <br /><br />
                            Our organization is made up of talented and accomplished students from a wide range of majors, backgrounds, and career aspirations. We believe that diversity of thought and experience strengthens our community, and we are proud to foster an environment where individuals are challenged, supported, and inspired to become the best version of themselves.
                            <br /><br />
                            Since joining BPAD, I have found a community that has shaped my confidence, strengthened my leadership, and provided me with meaningful, lifelong relationships. The culture of accountability, ambition, and support within BPAD is truly unmatched.
                            <br /><br />
                            I encourage you to continue exploring our organization and to attend one of our recruitment events to experience firsthand what makes BPAD so special. Thank you again for visiting our website. We look forward to meeting you soon.
                            <br /><br />
                            Sincerely,
                            <br />
                            Meg Newcomb
                        </p>

                    </div>
                </div>
            </div>
            <div className="our-pillars-section" ref={pillarsSectionRef}>
                <div className="our-pillars-content" ref={pillarsRef}>
                    <h2>OUR PILLARS</h2>
                    <p>Here at BPAD, we are guided by our five pillars.</p>
                </div>
            </div>
            <div className="pillars-section">
                <div className="pillar pillar-left pillar-service" ref={el => pillarRefs.current[0] = el}>
                    <img src={serviceImg} alt="Service" className="pillar-image" />
                    <div className="pillar-text">
                        <h3>SERVICE</h3>
                        <p className="pillar-description">We believe that leading our lives with small acts of kindness and a giving mindset ignites change in the world. Greatness isn’t what you have, it’s what you give! Service isn’t just going to a place to volunteer, it’s how you treat, act, and love others. BPAD offers many opportunities for service, including our winter break service trip, and our BPAD Impact day!</p>
                    </div>
                </div>
                <div className="pillar pillar-right pillar-inclusion" ref={el => pillarRefs.current[1] = el}>
                    <img src={inclusionImg} alt="Inclusion" className="pillar-image" />
                    <div className="pillar-text">
                        <h3>INCLUSION</h3>
                        <p className="pillar-description">BPAD welcomes members from all majors and all backgrounds. We believe that diversity is a strength. We embrace different perspectives and positive change. As leaders of tomorrow, we embrace people in our organization who have the courage to be different, and who treat others with kindness no matter who they are.</p>
                    </div>
                </div>
                <div className="pillar pillar-left pillar-leadership" ref={el => pillarRefs.current[2] = el}>
                    <img src={leadershipImg} alt="Leadership" className="pillar-image" />
                    <div className="pillar-text">
                        <h3>LEADERSHIP</h3>
                        <p className="pillar-description">We define leadership as the ability to create a positive impact, to unify, and to empower those around you. BPAD offers 46 unique leadership positions, but reserves leadership as a quality that surpasses titles and roles. Our model for leadership is supported by our 5 pillars, and makes BPAD the organization that it is. We value all approaches to leadership, and empower all of our members to use their voices to lead.</p>
                    </div>
                </div>
                <div className="pillar pillar-right pillar-unity" ref={el => pillarRefs.current[3] = el}>
                    <img src={unityImg} alt="Unity" className="pillar-image" />
                    <div className="pillar-text">
                        <h3>UNITY</h3>
                        <p className="pillar-description">BPAD is so much more than just a professional development club. It’s a flourishing community and support group fit for a college student who is taking on the world by storm. Our membership boasts a unique family bond, fostering relationships that transpire beyond our years at Ohio State.</p>
                    </div>
                </div>
                <div className="pillar pillar-left pillar-curiosity" ref={el => pillarRefs.current[4] = el}>
                    <img src={curiosityImg} alt="Curiosity" className="pillar-image" />
                    <div className="pillar-text">
                        <h3>CURIOSITY</h3>
                        <p className="pillar-description">We embody our pillar of curiosity through the pursuit of knowledge, growth, and ambition. The ambitious act of asking questions guides our lives and goals. Our members are expected to dive into their studies as well as the professional world with this sense of curiosity. Our membership is a growth oriented community, learning from each other to better understand the world.</p>
                    </div>
                </div>
            </div>
            <div className="companies-section">
                <div className="separator"></div>
                <h2>COMPANIES WE'VE BROUGHT VALUE TO</h2>
                <div className="logos-container">
                    <img src={abercrombie} alt="Abercrombie" className="company-logo" ref={el => logoRefs.current[0] = el} />
                    <img src={accenture} alt="Accenture" className="company-logo" ref={el => logoRefs.current[1] = el} />
                    <img src={amazon} alt="Amazon" className="company-logo" ref={el => logoRefs.current[2] = el} />
                    <img src={bcg} alt="BCG" className="company-logo" ref={el => logoRefs.current[3] = el} />
                    <img src={bofa} alt="BofA" className="company-logo" ref={el => logoRefs.current[4] = el} />
                    <img src={capone} alt="Capital One" className="company-logo" ref={el => logoRefs.current[5] = el} />
                    <img src={deloitte} alt="Deloitte" className="company-logo" ref={el => logoRefs.current[6] = el} />
                    <img src={ey} alt="EY" className="company-logo" ref={el => logoRefs.current[7] = el} />
                    <img src={gap} alt="GAP" className="company-logo" ref={el => logoRefs.current[8] = el} />
                    <img src={goldman} alt="Goldman" className="company-logo" ref={el => logoRefs.current[9] = el} />
                    <img src={honda} alt="Honda" className="company-logo" ref={el => logoRefs.current[10] = el} />
                    <img src={jpm} alt="JP Morgan" className="company-logo" ref={el => logoRefs.current[11] = el} />
                    <img src={kroger} alt="Kroger" className="company-logo" ref={el => logoRefs.current[12] = el} />
                    <img src={levis} alt="Levis" className="company-logo" ref={el => logoRefs.current[13] = el} />
                    <img src={mckinsey} alt="Mckinsey" className="company-logo" ref={el => logoRefs.current[14] = el} />
                    <img src={microsoft} alt="Microsoft" className="company-logo" ref={el => logoRefs.current[15] = el} />
                    <img src={nationwide} alt="Nationwide" className="company-logo" ref={el => logoRefs.current[16] = el} />
                    <img src={oracle} alt="Oracle" className="company-logo" ref={el => logoRefs.current[17] = el} />
                    <img src={pwc} alt="PWC" className="company-logo" ref={el => logoRefs.current[18] = el} />
                    <img src={spacex} alt="Space X" className="company-logo" ref={el => logoRefs.current[19] = el} />
                </div>
            </div>
        </div>
    );
}
